export class DetailView {
    constructor() {
        // Create container - make it transparent to allow contact sheet to show through
        this.container = document.createElement('div');
        this.container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1000;
            opacity: 0;
            pointer-events: none;
            transform: scale(0.6);
            transform-origin: center center;
            overscroll-behavior: contain;
            background-color: transparent;
        `;
        
        // Track visibility state
        this.isVisible = false;
        
        // Track transition state to prevent overlapping animations
        this.isTransitioning = false;
        this.currentTransitionTimeline = null;

        // Contact sheet reference for controlling its close button
        this.contactSheet = null;

        // Create background overlay
        this.background = document.createElement('div');
        this.background.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            opacity: 0;
        `;

        // Create close button
        this.closeButton = document.createElement('button');
        this.closeButton.className = 'circular-btn circular-btn-close';
        this.closeButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="white"/>
            </svg>
        `;
        this.closeButton.setAttribute('aria-label', 'Close image details');

        // Create content wrapper
        this.content = document.createElement('div');
        this.content.style.cssText = `
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            opacity: 0;
            z-index: 1;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y;
            overscroll-behavior: contain;
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            -ms-touch-action: pan-y;
            user-select: none;
            -webkit-user-select: none;
            background-color: black;
        `;

        // Create image
        this.image = document.createElement('img');
        this.image.setAttribute('draggable', 'false');
        this.image.style.cssText = `
            width: 100%;
            height: auto;
            display: block;
            position: relative;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -webkit-user-drag: none;
            user-drag: none;
            user-select: none;
            margin-bottom: 20px;
        `;

        // Create title
        this.title = document.createElement('h2');
        this.title.style.cssText = `
            color: white;
            font-size: 16px;
            font-weight: 400;
            margin: 0 20px 4px;
            font-family: "IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace;
            text-transform: uppercase;
            font-variant: small-caps;
            letter-spacing: 0.06em;
        `;

        // Create description
        this.description = document.createElement('p');
        this.description.style.cssText = `
            color: white;
            margin: 8px 20px 10px;
            line-height: 1.5;
            font-family: "IBM Plex Sans Light", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        `;
        this.description.textContent = '';

        // Create location info
        this.locationInfo = document.createElement('div');
        this.locationInfo.style.cssText = `
            color: rgba(255, 255, 255, 0.7);
            margin: 3px 20px 10px;
            font-size: 12px;
            font-family: "IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        // Create product selection container
        this.productContainer = document.createElement('div');
        this.productContainer.style.cssText = `
            margin: 20px 15px 40px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 24px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            max-width: none;
            width: calc(100% - 30px);
        `;

        // Create product selection header
        this.productHeader = document.createElement('h3');
        this.productHeader.textContent = 'Available Prints';
        this.productHeader.style.cssText = `
            color: white;
            font-size: 12px;
            font-weight: 300;
            margin: 0 0 15px 0;
            font-family: "IBM Plex Sans Light", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            text-transform: uppercase;
            letter-spacing: 0.06em;
        `;

        // Create product type dropdown
        this.productDropdown = document.createElement('select');
        this.productDropdown.style.cssText = `
            width: 100%;
            background: rgba(0, 0, 0, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            color: white;
            padding: 15px 35px 15px 16px;
            font-size: 16px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            margin-bottom: 15px;
            cursor: pointer;
            outline: none;
            transition: border-color 0.2s ease, background 0.2s ease;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
            background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>');
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 10px 6px;
        `;

        // Add CSS for dropdown options with better cross-browser support
        const dropdownStyle = document.createElement('style');
        dropdownStyle.textContent = `
            .product-dropdown {
                background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>') !important;
                background-repeat: no-repeat !important;
                background-position: right 12px center !important;
                background-size: 10px 6px !important;
            }
            .product-dropdown:focus {
                background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>') !important;
                background-repeat: no-repeat !important;
                background-position: right 12px center !important;
                background-size: 10px 6px !important;
            }
            .product-dropdown option {
                background: rgba(0, 0, 0, 0.95) !important;
                color: white !important;
                padding: 12px 16px !important;
                font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace !important;
                font-size: 16px !important;
                line-height: 1.4 !important;
                min-height: 44px !important;
            }
            .product-dropdown option:checked {
                background: rgba(255, 255, 255, 0.15) !important;
                color: white !important;
            }
            .product-dropdown option:hover {
                background: rgba(255, 255, 255, 0.1) !important;
            }
            /* Safari-specific fixes */
            @supports (-webkit-appearance: none) {
                .product-dropdown option {
                    background-color: rgba(0, 0, 0, 0.95) !important;
                    color: white !important;
                }
            }
        `;
        document.head.appendChild(dropdownStyle);

        // Add class to ensure arrow persistence
        this.productDropdown.className = 'product-dropdown';

        // Style dropdown options with arrow persistence
        this.productDropdown.addEventListener('focus', () => {
            this.productDropdown.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            this.productDropdown.style.background = 'rgba(0, 0, 0, 0.9)';
            // Ensure arrow stays visible
            this.productDropdown.style.backgroundImage = 'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>\')';
        });

        this.productDropdown.addEventListener('blur', () => {
            this.productDropdown.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            this.productDropdown.style.background = 'rgba(0, 0, 0, 0.8)';
            // Ensure arrow stays visible
            this.productDropdown.style.backgroundImage = 'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>\')';
        });

        // Ensure arrow persists after any interaction
        this.productDropdown.addEventListener('change', () => {
            setTimeout(() => {
                this.productDropdown.style.backgroundImage = 'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 8"><path fill="white" d="M6 8L0 2h12z"/></svg>\')';
                this.productDropdown.style.backgroundRepeat = 'no-repeat';
                this.productDropdown.style.backgroundPosition = 'right 12px center';
                this.productDropdown.style.backgroundSize = '10px 6px';
            }, 10);
        });

        // Create product details container
        this.productDetails = document.createElement('div');
        this.productDetails.style.cssText = `
            margin-bottom: 20px;
        `;

        // Create price display
        this.priceDisplay = document.createElement('div');
        this.priceDisplay.style.cssText = `
            color: white;
            font-size: 18px;
            font-weight: 300;
            margin-bottom: 10px;
            font-family: "IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        // Create product description
        this.productDescription = document.createElement('div');
        this.productDescription.style.cssText = `
            color: rgba(255, 255, 255, 0.8);
            font-size: 14px;
            line-height: 1.4;
            margin-bottom: 15px;
            font-family: "IBM Plex Sans Light", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        `;

        // Create SKU display
        this.skuDisplay = document.createElement('div');
        this.skuDisplay.style.cssText = `
            color: rgba(255, 255, 255, 0.6);
            font-size: 12px;
            margin-bottom: 20px;
            font-family: "IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        // Create inquiry container (holds both button and form states)
        this.inquiryContainer = document.createElement('div');
        this.inquiryContainer.style.cssText = `
            width: 100%;
            overflow: hidden;
        `;

        // Create purchase button
        this.purchaseButton = document.createElement('button');
        this.purchaseButton.textContent = 'Inquire about this image';
        this.purchaseButton.style.cssText = `
            background: transparent;
            color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            padding: 12px 24px;
            cursor: pointer;
            font-size: 14px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            width: 100%;
            transition: all 0.2s ease;
            outline: none;
        `;

        // Purchase button hover effects
        this.purchaseButton.addEventListener('mouseenter', () => {
            if (!this.formExpanded) {
                this.purchaseButton.style.background = 'rgba(255, 255, 255, 0.05)';
                this.purchaseButton.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                this.purchaseButton.style.color = 'rgba(255, 255, 255, 1)';
            }
        });

        this.purchaseButton.addEventListener('mouseleave', () => {
            if (!this.formExpanded) {
                this.purchaseButton.style.background = 'transparent';
                this.purchaseButton.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                this.purchaseButton.style.color = 'rgba(255, 255, 255, 0.8)';
            }
        });

        // Purchase button click handler - expand to form
        this.purchaseButton.addEventListener('click', () => {
            this.expandInquiryForm();
        });

        // Create inquiry form (hidden by default)
        this.createInquiryForm();

        // Track form state
        this.formExpanded = false;
        this.formSubmitting = false;

        // Assemble product container
        this.productDetails.appendChild(this.priceDisplay);
        this.productDetails.appendChild(this.productDescription);
        this.productDetails.appendChild(this.skuDisplay);

        // Assemble inquiry container
        this.inquiryContainer.appendChild(this.purchaseButton);

        this.productContainer.appendChild(this.productHeader);
        this.productContainer.appendChild(this.productDropdown);
        this.productContainer.appendChild(this.productDetails);
        this.productContainer.appendChild(this.inquiryContainer);

        // Store current product data
        this.currentImageData = null;
        this.selectedProductIndex = 0;

        // Create navigation buttons for desktop and mobile
        this.createNavigationButtons();

        // Assemble the DOM
        this.content.appendChild(this.image);
        this.content.appendChild(this.title);
        this.content.appendChild(this.locationInfo);
        this.content.appendChild(this.description);
        this.content.appendChild(this.productContainer);
        this.container.appendChild(this.background);
        this.container.appendChild(this.content);
        this.container.appendChild(this.closeButton);
        
        // Add class for context menu detection
        this.container.className = 'detail-view-container';
        
        document.body.appendChild(this.container);

        // Event handlers
        this.closeButton.addEventListener('click', () => {
            console.log('DetailView: Close button clicked. Routing to NC.');
            if (this.navigationController) {
                this.navigationController.navigate('close-detail');
            } else {
                // Fallback if NavigationController not available
                this.hide();
            }
        });
        
        // Product dropdown change handler
        this.productDropdown.addEventListener('change', () => {
            this.selectedProductIndex = parseInt(this.productDropdown.value);
            this.updateProductDetails();
        });

        // Close when clicking outside the content area (on the background)
        this.background.addEventListener('click', () => this.hide());
        
        // Prevent content clicks from bubbling up to background
        this.content.addEventListener('click', (e) => e.stopPropagation());

        // Initialize DetailView's own gesture handling
        this.setupGestureHandling();
        
        // Create resize handler to adjust view on window resize
        this.resizeHandler = () => {
            if (!this.container || this.container.style.display === 'none') return;
            
            // 35mm contact sheet aspect ratio (2:3)
            const imageAspectRatio = 2 / 3;
            
            // Reserve minimal space for just title and a hint of description (user scrolls for more)
            const textSpaceHeight = 80; // Much smaller - just title + small margin
            
            // Calculate available height for the image
            const availableImageHeight = window.innerHeight - textSpaceHeight;
            
            // Calculate the width needed to show the full image at this height
            const calculatedWidth = availableImageHeight * imageAspectRatio;
            
            // Ensure we don't exceed viewport width (with some margin)
            const maxAllowedWidth = window.innerWidth * 0.9;
            const finalWidth = Math.min(calculatedWidth, maxAllowedWidth);
            
            // Apply styles - dynamic width
            this.content.style.width = `${finalWidth}px`;
            this.content.style.margin = '0 auto';
            this.content.style.overflow = 'auto';
            
            // Style based on whether we're mobile-like (narrow) or desktop-like
            if (window.innerWidth <= 480) {
                // Very narrow - likely mobile - use original mobile styling
                this.content.style.width = '100%';
                this.content.style.height = '100%';
                this.content.style.borderRadius = '0';
                this.content.style.boxShadow = 'none';
                this.background.style.backgroundColor = 'rgba(0, 0, 0, 1)';
            } else {
                // Wider - desktop-like - use dynamic width and full height
                this.content.style.height = '100vh';
                this.content.style.borderRadius = '8px';
                this.content.style.boxShadow = '0 0 25px rgba(0, 0, 0, 0.5)';
                this.background.style.backgroundColor = 'rgba(0, 0, 0, 0.75)';
            }
            
            // Update navigation buttons position based on screen size, but don't show them yet
            this.updateButtonPositions();
        };
    }
    
    formatTitle(filename) {
        return filename
            .replace(/\.[^/.]+$/, '') // Remove extension
            .replace(/-/g, ' ') // Replace hyphens with spaces
            .replace(/([A-Z])/g, ' $1') // Add space before capitals
            .trim();
    }
    
    show(imageData, camera, onClose, getFrustumForClose, positioningData = null, currentGridPos = null, onNavigate = null) {
        return new Promise(async resolve => {
            this.onClose = onClose;
            this.camera = camera;
            this.isVisible = true;
            this.getFrustumForClose = getFrustumForClose;
            this.positioningData = positioningData;
            this.onNavigateCallback = onNavigate;

            // Reset form state when showing detail view
            this.formExpanded = false;
            this.formSubmitting = false;
            if (this.inquiryForm) {
                this.inquiryForm.style.display = 'none';
                this.inquiryForm.style.opacity = '0';
                this.inquiryForm.style.height = '0px';
            }
            if (this.purchaseButton) {
                this.purchaseButton.style.opacity = '1';
                this.purchaseButton.style.height = 'auto';
                this.purchaseButton.style.padding = '12px 24px';
            }

            // Set up navigation state if grid position provided
            if (currentGridPos) {
                this.navigationState.currentLinearIndex = this.gridToLinearIndex(currentGridPos.row, currentGridPos.col);
            }
            // Store original camera settings - but only use if no getFrustumForClose callback
            this.originalSettings = {
                left: camera.left,
                right: camera.right,
                top: camera.top,
                bottom: camera.bottom
            };
            // Adjust container width based on screen size - use the shared resize handler
            this.resizeHandler();
            // --- SURGICAL FIX: Set chevron state before fade-in to prevent flash ---
            this.updateNavigationButtonStates();
            // --- END FIX ---
            // Add resize listener
            window.addEventListener('resize', this.resizeHandler);
            // Update content - use preloaded image if available
            let decodePromise = null;
            if (imageData.domImage && imageData.domImage.src) {
                // Clone the preloaded DOM image to ensure instant display
                const newImage = imageData.domImage.cloneNode(true);
                newImage.style.cssText = this.image.style.cssText;
                newImage.setAttribute('draggable', 'false');
                newImage.style.opacity = '0'; // Start hidden
                // Ensure the image is actually in the content before trying to replace it
                if (this.image.parentNode === this.content) {
                    this.content.replaceChild(newImage, this.image);
                } else {
                    // If image is not in content, just append the new one
                    this.content.appendChild(newImage);
                }
                this.image = newImage;
                // Wait for decode if supported
                if (this.image.decode) {
                    decodePromise = this.image.decode().catch(() => {});
                } else {
                    decodePromise = new Promise(res => {
                        if (this.image.complete) res();
                        else this.image.onload = res;
                    });
                }
            } else {
                // Fallback to standard image loading if the preloaded image is missing.
                // Ensure image is in the DOM if not already
                if (!this.image.parentNode) {
                    this.content.appendChild(this.image);
                }
                this.image.src = imageData.url;
                this.image.style.opacity = '0'; // Start hidden
                if (this.image.decode) {
                    decodePromise = this.image.decode().catch(() => {});
                } else {
                    decodePromise = new Promise(res => {
                        if (this.image.complete) res();
                        else this.image.onload = res;
                    });
                }
            }
            // Use metadata title if available, otherwise format filename
            if (imageData.metadata && imageData.metadata.title) {
                this.title.textContent = imageData.metadata.title;
            } else {
                this.title.textContent = this.formatTitle(imageData.filename);
            }
            // Use metadata description if available, otherwise keep default
            if (imageData.metadata && imageData.metadata.description) {
                this.description.textContent = imageData.metadata.description;
            }
            // Store current image data for product interface
            this.currentImageData = imageData;
            // Update location info if available
            this.updateLocationInfo(imageData.metadata);
            // Update product interface
            this.updateProductInterface(imageData.metadata);
            // Enable interaction for DetailView gestures
            this.container.style.pointerEvents = 'auto';
            // Safety: Ensure contact sheet close button is hidden when detail view opens
            this.hideContactSheetCloseButton();
            // Wait for image to be decoded and painted before animating in
            if (decodePromise) {
                await decodePromise;
            }
            // Create master timeline
            const tl = gsap.timeline({
                onComplete: () => {
                    // Position navigation buttons first
                    this.updateButtonPositions();
                    resolve(); // Resolve the promise when the animation completes
                }
            });
            // Calculate zoom amounts and positioning - match ContactSheet responsive zoom levels
            // Use the same responsive logic as calculateZoomFrustum
            const zoomFactor = window.innerWidth <= 768 ? 1.15 : 1.25;
            let startTransform, startPosition;
            if (positioningData && positioningData.startPosition && positioningData.startSize) {
                // Force a layout with actual responsive dimensions to get accurate measurements
                this.container.style.display = 'block';
                this.container.style.opacity = '0';
                this.container.style.transform = 'scale(1)'; // Ensure we measure at actual size
                // Apply the actual responsive layout that will be used
                this.resizeHandler();
                // Force a layout pass to get real dimensions
                this.container.offsetHeight;
                // Get the actual rendered size and position of the detail view image
                const imageRect = this.image.getBoundingClientRect();
                const actualImageHeight = imageRect.height;
                const actualImageCenterY = imageRect.top + (imageRect.height / 2);
                // Calculate the scale needed to match the source image size
                const scaleToMatchSource = positioningData.startSize.height / actualImageHeight;
                // Calculate Y offset to match source image position
                // Use the actual image center position, not viewport center
                const offsetY = positioningData.startPosition.y - actualImageCenterY;
                startTransform = `translate(0px, ${offsetY}px) scale(${scaleToMatchSource})`;
                startPosition = 'translate(0px, 0px) scale(1)';
                // Reset for the animation
                this.container.style.display = 'none';
                this.container.style.transform = 'scale(0.6)'; // Reset to initial state
            } else {
                // Fallback to original behavior - match responsive zoom levels
                const startScale = window.innerWidth <= 768 ? 0.5 : 0.4;
                startTransform = `scale(${startScale})`;
                startPosition = 'scale(1)';
            }
            // Initial states
            gsap.set(this.container, {
                display: 'block',
                opacity: 0,
                transform: startTransform,
                transformOrigin: 'center center'
            });
            gsap.set([this.closeButton, this.content], { autoAlpha: 0 });
            gsap.set(this.background, { opacity: 0 });
            gsap.set([this.leftNavButton, this.rightNavButton], { autoAlpha: 0 });
            // Animate image opacity in as part of the content fade-in
            tl.to(camera, {
                left: this.originalSettings.left / zoomFactor,
                right: this.originalSettings.right / zoomFactor,
                top: this.originalSettings.top / zoomFactor,
                bottom: this.originalSettings.bottom / zoomFactor,
                duration: 0.65,
                ease: 'power2.inOut',
                overwrite: 'auto',
                onUpdate: () => camera.updateProjectionMatrix()
            })
            .to(this.container, {
                opacity: 1,
                transform: startPosition,
                duration: 0.65,
                ease: 'power2.inOut'
            }, 0)
            .to(this.background, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.inOut'
            }, 0)
            .to([this.closeButton, this.content], {
                autoAlpha: 1,
                duration: 0.3,
                ease: 'power2.out'
            }, '-=0.2')
            .to(this.image, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            }, '-=0.2');
            // Fade in nav chevrons as last step (desktop only)
            if (window.innerWidth > 768) {
                tl.to([this.leftNavButton, this.rightNavButton], {
                    autoAlpha: 1,
                    duration: 0.3,
                    ease: 'power2.out',
                    onStart: () => {
                        this.updateNavigationButtonStates();
                    }
                }, '+=0.05');
            }
            // Reset transition state
            this.isTransitioning = false;
            this.currentTransitionTimeline = null;
        });
    }

    // Update image without restarting the animation
    updateImage(newImage) {
        if (!this.isVisible || !this.image || !this.image.parentNode) return;
        
        const newImageElement = newImage.cloneNode(true);
        newImageElement.style.cssText = this.image.style.cssText;
        this.content.replaceChild(newImageElement, this.image);
        this.image = newImageElement;
        this.currentTransitionTimeline = null;
    }

    // Convert grid position to linear index (0-35)
    gridToLinearIndex(row, col, columns = 6) {
        return row * columns + col;
    }

    // Convert linear index to grid position
    linearToGridIndex(index, columns = 6) {
        const row = Math.floor(index / columns);
        const col = index % columns;
        return { row, col };
    }

    // Transition to new image with iOS-style slide animation
    async transitionToNewImage(imageData, direction, keyPressed) {
        if (!this.isVisible) return;

        // Close form if expanded before transitioning
        if (this.formExpanded) {
            this.collapseInquiryForm();
        }

        // 1. Handle rapid navigation / cancellation
        if (this.isTransitioning) {
            // If we have a running timeline, kill it and clean up DOM
            if (this.currentTransitionTimeline) {
                this.currentTransitionTimeline.kill();
                this.cleanupTransition();
            }
            // Always increment transition ID to invalidate any pending async loads from previous attempts
            this.transitionId = (this.transitionId || 0) + 1;
        }

        this.isTransitioning = true;
        const myTransitionId = this.transitionId = (this.transitionId || 0) + 1;

        // Keep NavigationController-aligned metadata current immediately
        this.currentImageData = imageData;
        
        // Reset scroll to top first
        if (this.content.scrollTop > 0) {
            gsap.to(this.content, {
                scrollTop: 0,
                duration: 0.2,
                ease: "power2.out"
            });
        }
        
        // Create new image element
        let newImage;
        if (imageData.domImage) {
            newImage = imageData.domImage.cloneNode(true);
        } else {
            newImage = document.createElement('img');
            newImage.src = imageData.url;
        }
    
        newImage.style.cssText = this.image.style.cssText;
        newImage.style.position = 'absolute';
        newImage.style.zIndex = '10';
        
        // Ensure proper positioning context for transitions
        const imageParent = this.image.parentNode;
        if (imageParent && imageParent.style.position !== 'relative') {
            imageParent.style.position = 'relative';
            imageParent.style.overflow = 'hidden';
        }
        
        if (this.image.style.position !== 'relative') {
            this.image.style.position = 'relative';
        }

        // Wait for decode ONLY if not already complete (cache hit)
        // This prevents the "snap" delay on cached images
        if (!imageData.domImage && !newImage.complete) {
            try { newImage.decoding = 'sync'; } catch (_) {}
            if (typeof newImage.decode === 'function') {
                try { await newImage.decode(); } catch (_) {}
            } else {
                await new Promise(res => {
                    newImage.onload = () => res();
                    newImage.onerror = () => res();
                });
            }
        }

        // Check if this transition was cancelled while we were waiting/decoding
        if (this.transitionId !== myTransitionId) {
            // We have been superseded by a newer navigation command. Abort.
            return;
        }

        // Calculate slide direction with explicit properties
        let startPosition, endPosition, finalPosition;
        
        switch (keyPressed) {
            case 'ArrowLeft':
                startPosition = { x: '-100%', y: '0%' };
                endPosition = { x: '100%', y: '0%' };
                finalPosition = { x: '0%', y: '0%' };
                break;
            case 'ArrowRight':
                startPosition = { x: '100%', y: '0%' };
                endPosition = { x: '-100%', y: '0%' };
                finalPosition = { x: '0%', y: '0%' };
                break;
            case 'ArrowUp':
                startPosition = { x: '0%', y: '-100%' };
                endPosition = { x: '0%', y: '100%' };
                finalPosition = { x: '0%', y: '0%' };
                break;
            case 'ArrowDown':
                startPosition = { x: '0%', y: '100%' };
                endPosition = { x: '0%', y: '-100%' };
                finalPosition = { x: '0%', y: '0%' };
                break;
        }
        
        // Set new image start position using explicit x/y properties
        gsap.set(newImage, {
            x: startPosition.x,
            y: startPosition.y,
            opacity: 0
        });
        
        // Insert new image after current image
        if (this.image.parentNode) {
            this.image.parentNode.insertBefore(newImage, this.image.nextSibling);
        } else {
            // If current image has no parent, add new image to content
            this.content.appendChild(newImage);
        }
        
        // Create and store the transition timeline
        this.currentTransitionTimeline = gsap.timeline({
            onComplete: () => {
                this.completeTransition(newImage);
            }
        });
        
        // Faster transition for rapid navigation
        const transitionDuration = 0.25; // Reduced from 0.4
        const contentFadeDuration = 0.15; // Reduced from 0.2
        
        // Fade out current content and slide out current image
        this.currentTransitionTimeline
        .to([this.title, this.description, this.locationInfo, this.productContainer], {
            opacity: 0,
            duration: contentFadeDuration,
            ease: "power2.out"
        })
        .to(this.image, {
            x: endPosition.x,
            y: endPosition.y,
            opacity: 0,
            duration: transitionDuration,
            ease: "power2.inOut"
        }, 0)
        // Slide in and fade in new image
        .to(newImage, {
            x: finalPosition.x,
            y: finalPosition.y,
            opacity: 1,
            duration: transitionDuration,
            ease: "power2.inOut"
        }, 0)
        // Update title and description text when content is fully faded out
        .call(() => {
            // Use metadata title if available, otherwise format filename
            if (imageData.metadata && imageData.metadata.title) {
                this.title.textContent = imageData.metadata.title;
            } else {
                this.title.textContent = this.formatTitle(imageData.filename);
            }
            
            // Use metadata description if available, otherwise keep current
            if (imageData.metadata && imageData.metadata.description) {
                this.description.textContent = imageData.metadata.description;
            }
            
            // Update location and product info for new image
            this.updateLocationInfo(imageData.metadata);
            this.updateProductInterface(imageData.metadata);
        }, null, contentFadeDuration)
        // Fade in content
        .to([this.title, this.description, this.locationInfo, this.productContainer], {
            opacity: 1,
            duration: contentFadeDuration,
            ease: "power2.out"
        }, contentFadeDuration);
    }
    
    // Clean up transition state and elements
    cleanupTransition() {
        // Robust cleanup: Remove ANY image that isn't the main one, especially those with absolute positioning
        // which cause layout flips if left behind.
        if (this.image && this.image.parentNode) {
            const allImages = this.image.parentNode.querySelectorAll('img');
            allImages.forEach(img => {
                if (img !== this.image) {
                    img.remove();
                }
            });
        }
        
        // Force reset current image styles to ensure it's in flow
        if (this.image) {
            this.image.style.position = 'relative';
            this.image.style.zIndex = 'auto';
            // Clear GSAP transforms to bring it back to origin
            gsap.set(this.image, { x: 0, y: 0, opacity: 1, clearProps: "transform" });
        }
    }
    
    // Complete the transition and clean up
    completeTransition(newImage) {
        // Remove old image and any other stray images from rapid navigation
        if (newImage.parentNode) {
            const allImages = newImage.parentNode.querySelectorAll('img');
            allImages.forEach(img => {
                if (img !== newImage) img.remove();
            });
        } else if (this.image && this.image.parentNode) {
            this.image.remove();
        }

        // Update reference and reset styles
        this.image = newImage;
        this.image.style.position = 'relative';
        this.image.style.zIndex = 'auto';
        gsap.set(this.image, { x: 0, y: 0, opacity: 1 });

        this.isTransitioning = false;
        this.currentTransitionTimeline = null;
    }

    hide() {
        return new Promise(resolve => {
            this.isVisible = false;

            // Close form if expanded before hiding
            if (this.formExpanded) {
                this.formExpanded = false; // Reset state immediately
                this.inquiryForm.style.display = 'none';
                this.inquiryForm.style.opacity = '0';
                this.inquiryForm.style.height = '0px';
                this.purchaseButton.style.opacity = '1';
                this.purchaseButton.style.height = 'auto';
                this.purchaseButton.style.padding = '12px 24px';
            }

            // Hide navigation buttons immediately
            this.updateNavigationButtons();
            
            let endTransform;
            
            if (this.positioningData && this.positioningData.endPosition && this.positioningData.endSize) {
                // Get the actual current rendered size and position of the detail view image
                const imageRect = this.image.getBoundingClientRect();
                const actualImageHeight = imageRect.height;
                const actualImageCenterY = imageRect.top + (imageRect.height / 2);
                
                // Calculate the scale needed to match the source image size
                const scaleToMatchSource = this.positioningData.endSize.height / actualImageHeight;
                
                // Calculate Y offset to match source image position
                // Use the actual current image center position
                const offsetY = this.positioningData.endPosition.y - actualImageCenterY;
                
                endTransform = `translate(0px, ${offsetY}px) scale(${scaleToMatchSource})`;
            } else {
                // Fallback to original behavior - match responsive zoom levels
                const endScale = window.innerWidth <= 768 ? 0.5 : 0.4;
                endTransform = `scale(${endScale})`;
            }
            
            // Get correct frustum values - use callback if available, otherwise stored values
            const targetFrustum = this.getFrustumForClose ? this.getFrustumForClose() : this.originalSettings;
            
            const tl = gsap.timeline({
                onComplete: () => {
                    this.container.style.pointerEvents = 'none';
                    // Reset scroll position to top for next time detail view is opened
                    this.content.scrollTop = 0;
                    // Safety: Ensure contact sheet close button is restored when detail view closes
                    this.showContactSheetCloseButton();
                    if (this.onClose) this.onClose();
                    resolve(); // Resolve the promise when the animation completes
                }
            });
            
            // First fade out content, close button, and navigation buttons
            tl.to([this.closeButton, this.content, this.leftNavButton, this.rightNavButton], {
                autoAlpha: 0,
                duration: 0.3,
                ease: 'power2.in',
                onStart: () => {
                    this.leftEdgeZone.style.pointerEvents = 'none';
                    this.rightEdgeZone.style.pointerEvents = 'none';
                }
            })
            // Scale down detail view while zooming camera back
            .to(this.container, {
                opacity: 0,
                transform: endTransform,
                duration: 0.65,
                ease: 'power2.inOut'
            }, 0)
            .to(this.background, {
                opacity: 0,
                duration: 0.5,
                ease: 'power2.inOut'
            }, 0)
            // Zoom camera back in - use current correct frustum values
            .to(this.camera, {
                left: targetFrustum.left,
                right: targetFrustum.right,
                top: targetFrustum.top,
                bottom: targetFrustum.bottom,
                duration: 0.65,
                ease: 'power2.inOut',
                overwrite: 'auto',
                onUpdate: () => this.camera.updateProjectionMatrix()
            }, 0);
            
            window.removeEventListener('resize', this.resizeHandler);
        });
    }

    // Set NavigationController reference
    setNavigationController(navigationController) {
        this.navigationController = navigationController;
    }

    dispose() {
        this.isVisible = false;
        
        // Clean up any ongoing transitions
        if (this.currentTransitionTimeline) {
            this.currentTransitionTimeline.kill();
            this.currentTransitionTimeline = null;
        }
        this.isTransitioning = false;
        
        // Clean up gesture handling
        if (this.hammer) {
            this.hammer.destroy();
        }
        
        if (this.container && this.touchHandler) {
            this.container.removeEventListener('touchstart', this.touchHandler);
            this.container.removeEventListener('gesturechange', this.touchHandler);
        }
        
        // Remove DOM elements
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        
        if (this.closeButton && this.closeButton.parentNode) {
            this.closeButton.parentNode.removeChild(this.closeButton);
        }
        
        // Remove navigation buttons
        if (this.leftNavButton && this.leftNavButton.parentNode) {
            this.leftNavButton.parentNode.removeChild(this.leftNavButton);
        }
        
        if (this.rightNavButton && this.rightNavButton.parentNode) {
            this.rightNavButton.parentNode.removeChild(this.rightNavButton);
        }
        
        // Remove tooltips
        if (this.leftTooltip && this.leftTooltip.parentNode) {
            this.leftTooltip.parentNode.removeChild(this.leftTooltip);
        }
        
        if (this.rightTooltip && this.rightTooltip.parentNode) {
            this.rightTooltip.parentNode.removeChild(this.rightTooltip);
        }
        
        // Remove mobile navigation elements
        if (this.mobileLeftChevron && this.mobileLeftChevron.parentNode) {
            this.mobileLeftChevron.parentNode.removeChild(this.mobileLeftChevron);
        }
        
        if (this.mobileRightChevron && this.mobileRightChevron.parentNode) {
            this.mobileRightChevron.parentNode.removeChild(this.mobileRightChevron);
        }
        
        if (this.mobileInstructions && this.mobileInstructions.parentNode) {
            this.mobileInstructions.parentNode.removeChild(this.mobileInstructions);
        }
        
        if (this.leftEdgeZone && this.leftEdgeZone.parentNode) {
            this.leftEdgeZone.parentNode.removeChild(this.leftEdgeZone);
        }
        
        if (this.rightEdgeZone && this.rightEdgeZone.parentNode) {
            this.rightEdgeZone.parentNode.removeChild(this.rightEdgeZone);
        }
        
        window.removeEventListener('resize', this.resizeHandler);
    }

    setupGestureHandling() {
        // Create Hammer.js instance for DetailView-specific gestures
        this.hammer = new Hammer(this.container);
        
        // Configure pinch recognizer
        this.hammer.get('pinch').set({
            enable: true,
            threshold: 0.1,
            pointers: 2
        });

        // Configure swipe recognizer for horizontal navigation
        this.hammer.get('swipe').set({
            direction: Hammer.DIRECTION_HORIZONTAL,
            threshold: 30,
            velocity: 0.3
        });
        
        // Prevent default touch behaviors
        this.container.style.webkitTouchCallout = 'none';
        this.container.style.webkitUserSelect = 'none';
        this.container.style.webkitTapHighlightColor = 'rgba(0,0,0,0)';
        
        // Allow vertical scrolling on content, prevent horizontal
        this.content.style.touchAction = 'pan-y';
        
        // Add scroll event listener to dismiss tutorial on scroll
        this.content.addEventListener('scroll', () => {
            if (this.navigationController) {
                this.navigationController.handleTutorialInteraction();
            }
        }, { passive: true });
        
        // Pinch gesture state
        this.pinchState = {
            isActive: false,
            initialScale: 1,
            threshold: 0.8
        };

        // Navigation state for linear sequence
        this.navigationState = {
            currentLinearIndex: 0,
            totalImages: 36,
            rows: 6,
            columns: 6
        };
        
        // Pinch event handlers
        this.hammer.on('pinchstart', (event) => {
            this.pinchState.isActive = true;
            this.pinchState.initialScale = event.scale;
        });
        
        this.hammer.on('pinchin', (event) => {
            if (!this.pinchState.isActive) return;
            
            const scaleRatio = event.scale / this.pinchState.initialScale;
            
            if (scaleRatio < this.pinchState.threshold) {
                this.pinchState.isActive = false;
                
                console.log('DetailView: Pinch-in detected. Routing to NC.');
                if (this.navigationController) {
                    this.navigationController.navigate('close-detail', { source: 'pinch' });
                } else {
                    // Fallback if NavigationController not available
                    this.hide();
                }
            }
        });
        
        this.hammer.on('pinchend', () => {
            this.pinchState.isActive = false;
        });

        // Swipe event handlers for linear navigation
        this.hammer.on('swipeleft', (event) => {
            console.log('DetailView: Swipe detected. Routing to NC.');
            if (this.navigationController) {
                this.navigationController.navigate('right', { source: 'swipe' });
            } else {
                this.navigateLinear('next');
            }
        });

        this.hammer.on('swiperight', (event) => {
            console.log('DetailView: Swipe detected. Routing to NC.');
            if (this.navigationController) {
                this.navigationController.navigate('left', { source: 'swipe' });
            } else {
                this.navigateLinear('previous');
            }
        });
        
        // Multi-touch event handlers for additional gesture prevention
        this.touchHandler = (e) => {
            if (e.touches && e.touches.length >= 2) {
                e.preventDefault();
            }
        };
        
        this.container.addEventListener('touchstart', this.touchHandler, { passive: false });
        this.container.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });
    }

    // Navigate through images in linear sequence (for mobile)
    navigateLinear(direction) {
        if (!this.isVisible || !this.onNavigateCallback) return;

        const currentIndex = this.navigationState.currentLinearIndex;
        let targetIndex;

        if (direction === 'next') {
            targetIndex = currentIndex + 1;
        } else if (direction === 'previous') {
            targetIndex = currentIndex - 1;
        } else {
            return;
        }

        // Check boundaries and show bounce if at edges
        if (targetIndex < 0 || targetIndex >= this.navigationState.totalImages) {
            this.showLinearBounceAnimation(direction);
            return;
        }

        // Convert linear index to grid position
        const targetGridPos = this.linearToGridIndex(targetIndex, this.navigationState.columns);
        
        // Update navigation state
        this.navigationState.currentLinearIndex = targetIndex;

        // Synchronize with NavigationController
        if (this.navigationController) {
            this.navigationController.updatePosition(targetGridPos.row, targetGridPos.col);
        }

        // Update navigation button states
        this.updateNavigationButtons();

        // Use the callback to update the ContactSheet and get new image data
        this.onNavigateCallback(targetGridPos, direction === 'next' ? 'ArrowRight' : 'ArrowLeft');
    }

    // Show bounce animation for linear navigation boundaries
    showLinearBounceAnimation(direction) {
        if (!this.isVisible) return;
        
        // Small bounce distance in pixels
        const bounceDistance = 15;
        const bounceDuration = 0.15;
        
        let bounceTarget = { x: 0, y: 0 };
        
        // Calculate bounce direction in screen space
        if (direction === 'next') {
            bounceTarget.x = -bounceDistance; // Bounce left when trying to go forward at end
        } else if (direction === 'previous') {
            bounceTarget.x = bounceDistance;  // Bounce right when trying to go back at beginning
        }
        
        // Animate the container with bounce effect
        gsap.killTweensOf(this.container);
        
        const tl = gsap.timeline();
        
        // Bounce out
        tl.to(this.container, {
            x: bounceTarget.x,
            y: bounceTarget.y,
            duration: bounceDuration,
            ease: "power2.out"
        })
        // Spring back
        .to(this.container, {
            x: 0,
            y: 0,
            duration: bounceDuration * 1.5,
            ease: "power2.out"
        });
    }

    // Show bounce animation when trying to navigate beyond boundaries
    showBounceAnimation(direction) {
        if (!this.isVisible) return;
        
        // Small bounce distance in pixels
        const bounceDistance = 15;
        const bounceDuration = 0.15;
        
        let bounceTarget = { x: 0, y: 0 };
        
        // Calculate bounce direction in screen space
        switch (direction) {
            case 'ArrowUp':
                bounceTarget.y = bounceDistance;
                break;
            case 'ArrowDown':
                bounceTarget.y = -bounceDistance;
                break;
            case 'ArrowLeft':
                bounceTarget.x = bounceDistance;
                break;
            case 'ArrowRight':
                bounceTarget.x = -bounceDistance;
                break;
        }
        
        // Animate the container with bounce effect
        gsap.killTweensOf(this.container);
        
        const tl = gsap.timeline();
        
        // Bounce out
        tl.to(this.container, {
            x: bounceTarget.x,
            y: bounceTarget.y,
            duration: bounceDuration,
            ease: "power2.out"
        })
        // Spring back
        .to(this.container, {
            x: 0,
            y: 0,
            duration: bounceDuration * 1.5,
            ease: "power2.out"
        });
    }

    // Set ContactSheet reference for close button control
    setContactSheet(contactSheet) {
        this.contactSheet = contactSheet;
    }

    // Safety function to hide contact sheet close button
    hideContactSheetCloseButton() {
        if (this.contactSheet && this.contactSheet.closeButton) {
            gsap.killTweensOf(this.contactSheet.closeButton);
            gsap.set(this.contactSheet.closeButton, {
                opacity: 0,
                pointerEvents: 'none'
            });
        }
    }

    // Safety function to show contact sheet close button
    showContactSheetCloseButton() {
        // NavigationController now handles close button visibility
        // This method kept for compatibility but does nothing
    }

    // Create navigation buttons for desktop
    createNavigationButtons() {
        // Left navigation button
        this.leftNavButton = document.createElement('button');
        this.leftNavButton.className = 'circular-btn circular-btn-nav';
        this.leftNavButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="white"/>
            </svg>
        `;
        this.leftNavButton.title = 'Previous image';
        this.leftNavButton.style.cssText = `
            position: fixed;
            left: 20px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1001;
            opacity: 0;
            pointer-events: none;
        `;

        // Right navigation button
        this.rightNavButton = document.createElement('button');
        this.rightNavButton.className = 'circular-btn circular-btn-nav';
        this.rightNavButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="white"/>
            </svg>
        `;
        this.rightNavButton.title = 'Next image';
        this.rightNavButton.style.cssText = `
            position: fixed;
            right: 20px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1001;
            opacity: 0;
            pointer-events: none;
        `;

        // Create tooltips with context menu aesthetic
        this.createNavigationTooltips();

        // Add hover effects with tooltip integration (desktop only)
        this.leftNavButton.addEventListener('mouseenter', () => {
            if (!this.leftNavButton.disabled && window.innerWidth > 768) {
                this.showTooltip(this.leftTooltip, this.leftNavButton, 'Previous Image');
            }
        });
        
        this.leftNavButton.addEventListener('mouseleave', () => {
            if (window.innerWidth > 768) {
                this.hideTooltip(this.leftTooltip);
            }
        });

        this.rightNavButton.addEventListener('mouseenter', () => {
            if (!this.rightNavButton.disabled && window.innerWidth > 768) {
                this.showTooltip(this.rightTooltip, this.rightNavButton, 'Next Image');
            }
        });
        
        this.rightNavButton.addEventListener('mouseleave', () => {
            if (window.innerWidth > 768) {
                this.hideTooltip(this.rightTooltip);
            }
        });

        // Click handlers - route through NavigationController for proper routing
        this.leftNavButton.addEventListener('click', () => {
            if (!this.leftNavButton.disabled) {
                console.log('DetailView: Nav button clicked. Routing to NC.');
                
                // Route through NavigationController if available, fallback to direct navigation
                if (this.navigationController) {
                    this.navigationController.navigate('left');
                } else {
                    this.navigateLinear('previous');
                }
            }
        });

        this.rightNavButton.addEventListener('click', () => {
            if (!this.rightNavButton.disabled) {
                console.log('DetailView: Nav button clicked. Routing to NC.');
                
                // Route through NavigationController if available, fallback to direct navigation
                if (this.navigationController) {
                    this.navigationController.navigate('right');
                } else {
                    this.navigateLinear('next');
                }
            }
        });

        // Create mobile edge zones for tap navigation
        this.createMobileEdgeZones();

        // Add to DOM - initially to container, will be repositioned by updateButtonPositions
        this.container.appendChild(this.leftNavButton);
        this.container.appendChild(this.rightNavButton);
    }

    // Create mobile edge zones for tap navigation
    createMobileEdgeZones() {
        this.leftEdgeZone = document.createElement('div');
        this.leftEdgeZone.style.cssText = `
            position: absolute;
            left: 0;
            top: 60px;
            width: 12%;
            height: calc(100% - 60px);
            z-index: 999;
            opacity: 0;
            pointer-events: none;
            background: transparent;
        `;

        this.rightEdgeZone = document.createElement('div');
        this.rightEdgeZone.style.cssText = `
            position: absolute;
            right: 0;
            top: 60px;
            width: 12%;
            height: calc(100% - 60px);
            z-index: 999;
            opacity: 0;
            pointer-events: none;
            background: transparent;
        `;

        this.leftEdgeZone.addEventListener('click', (e) => {
            e.stopPropagation();
            this.flashButton('left');
            this.handleMobileNavigation('left');
        });

        this.rightEdgeZone.addEventListener('click', (e) => {
            e.stopPropagation();
            this.flashButton('right');
            this.handleMobileNavigation('right');
        });

        // Add to content for mobile positioning
        this.content.appendChild(this.leftEdgeZone);
        this.content.appendChild(this.rightEdgeZone);
    }

    // Create tooltips matching context menu aesthetic
    createNavigationTooltips() {
        // Left tooltip
        this.leftTooltip = document.createElement('div');
        this.leftTooltip.style.cssText = `
            position: fixed;
            background: rgba(0, 0, 0, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            padding: 8px 12px;
            color: white;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            font-size: 14px;
            z-index: 2001;
            opacity: 0;
            transform: scale(0.9);
            pointer-events: none;
            transition: opacity 0.2s ease, transform 0.2s ease;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            white-space: nowrap;
        `;

        // Right tooltip
        this.rightTooltip = document.createElement('div');
        this.rightTooltip.style.cssText = `
            position: fixed;
            background: rgba(0, 0, 0, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            padding: 8px 12px;
            color: white;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            font-size: 14px;
            z-index: 2001;
            opacity: 0;
            transform: scale(0.9);
            pointer-events: none;
            transition: opacity 0.2s ease, transform 0.2s ease;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            white-space: nowrap;
        `;

        // Add to DOM
        document.body.appendChild(this.leftTooltip);
        document.body.appendChild(this.rightTooltip);
    }

    // Show tooltip with proper positioning
    showTooltip(tooltip, button, text) {
        if (!this.isVisible || window.innerWidth <= 768) return;
        
        tooltip.textContent = text;
        
        // Position tooltip relative to button
        const buttonRect = button.getBoundingClientRect();
        const tooltipOffset = 15; // Distance from button - much closer
        
        if (button === this.leftNavButton) {
            // Position to the right of left button
            tooltip.style.left = `${buttonRect.right + tooltipOffset}px`;
        } else {
            // Position to the left of right button
            const tooltipWidth = tooltip.offsetWidth || 120; // Estimate width if not measured
            tooltip.style.left = `${buttonRect.left - tooltipOffset - tooltipWidth}px`;
        }
        
        tooltip.style.top = `${buttonRect.top + (buttonRect.height / 2) - 15}px`; // Center vertically
        
        // Show tooltip with animation
        tooltip.style.opacity = '1';
        tooltip.style.transform = 'scale(1)';
    }

    // Hide tooltip with animation
    hideTooltip(tooltip) {
        tooltip.style.opacity = '0';
        tooltip.style.transform = 'scale(0.9)';
    }

    // Update navigation button states and positions based on current position
    updateNavigationButtons() {
        if (!this.leftNavButton || !this.rightNavButton || !this.isVisible) return;

        // Only update button states, don't hide them
        this.updateNavigationButtonStates();
    }

    // Update button positions relative to detail view container
    updateButtonPositions() {
        if (!this.leftNavButton || !this.rightNavButton || !this.isVisible) return;
        
        // Calculate vertical center of image (image height / 2)
        const imageHeight = this.image.offsetHeight;
        const imageCenterY = imageHeight / 2;
        
        if (window.innerWidth <= 768) {
            // Mobile - move buttons to content container and position absolutely
            if (this.leftNavButton.parentNode !== this.content) {
                this.content.appendChild(this.leftNavButton);
                this.content.appendChild(this.rightNavButton);
            }
            
            // Apply mobile nav button classes and position
            this.leftNavButton.className = 'circular-btn circular-btn-nav-mobile';
            this.leftNavButton.style.cssText = `
                position: absolute;
                left: 5px;
                top: ${imageCenterY}px;
                transform: translateY(-50%);
                z-index: 1001;
                pointer-events: auto;
            `;
            
            this.rightNavButton.className = 'circular-btn circular-btn-nav-mobile';
            this.rightNavButton.style.cssText = `
                position: absolute;
                right: 5px;
                top: ${imageCenterY}px;
                transform: translateY(-50%);
                z-index: 1001;
                pointer-events: auto;
            `;

            // Enable edge zones for mobile
            this.leftEdgeZone.style.pointerEvents = 'auto';
            this.rightEdgeZone.style.pointerEvents = 'auto';
            
        } else {
            // Desktop - position 30px outside the detail view container
            if (this.leftNavButton.parentNode !== this.container) {
                this.container.appendChild(this.leftNavButton);
                this.container.appendChild(this.rightNavButton);
            }
            
            // Apply desktop nav button classes
            this.leftNavButton.className = 'circular-btn circular-btn-nav';
            this.rightNavButton.className = 'circular-btn circular-btn-nav';
            
            // Calculate detail view container edges and image center position
            const contentRect = this.content.getBoundingClientRect();
            const imageRect = this.image.getBoundingClientRect();
            const leftPosition = contentRect.left - 30 - 50; // 30px outside + button width
            const rightPosition = contentRect.right + 30;    // 30px outside
            const buttonTop = imageRect.top + (imageRect.height / 2);
            
            this.leftNavButton.style.cssText = `
                position: fixed;
                left: ${leftPosition}px;
                top: ${buttonTop}px;
                transform: translateY(-50%);
                z-index: 1001;
                pointer-events: auto;
            `;
            
            this.rightNavButton.style.cssText = `
                position: fixed;
                left: ${rightPosition}px;
                top: ${buttonTop}px;
                transform: translateY(-50%);
                z-index: 1001;
                pointer-events: auto;
            `;

            // Disable edge zones for desktop
            this.leftEdgeZone.style.pointerEvents = 'none';
            this.rightEdgeZone.style.pointerEvents = 'none';
        }
    }

    // Update button states (enabled/disabled) based on current position
    updateNavigationButtonStates() {
        if (!this.leftNavButton || !this.rightNavButton) return;

        const currentIndex = this.navigationState.currentLinearIndex;
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === this.navigationState.totalImages - 1;

        // Helper: set opacity for mobile dimming
        const setButtonOpacity = (btn, enabled) => {
            if (window.innerWidth <= 768) {
                btn.style.opacity = enabled ? '1.0' : '0.3';
            } else {
                btn.style.opacity = '';
            }
        };

        // Update left button state
        if (isFirst) {
            this.leftNavButton.disabled = true;
            this.leftNavButton.querySelector('path:last-child').setAttribute('fill', '#666');
            setButtonOpacity(this.leftNavButton, false);
        } else {
            this.leftNavButton.disabled = false;
            this.leftNavButton.querySelector('path:last-child').setAttribute('fill', 'white');
            setButtonOpacity(this.leftNavButton, true);
        }

        // Update right button state
        if (isLast) {
            this.rightNavButton.disabled = true;
            this.rightNavButton.querySelector('path:last-child').setAttribute('fill', '#666');
            setButtonOpacity(this.rightNavButton, false);
        } else {
            this.rightNavButton.disabled = false;
            this.rightNavButton.querySelector('path:last-child').setAttribute('fill', 'white');
            setButtonOpacity(this.rightNavButton, true);
        }
    }

    // Update location info if available
    updateLocationInfo(metadata) {
        if (metadata && metadata.location && metadata.location.latitude && metadata.location.longitude) {
            const lat = metadata.location.latitude.toFixed(4);
            const lng = metadata.location.longitude.toFixed(4);
            this.locationInfo.textContent = `${lat}°, ${lng}°`;
            this.locationInfo.style.display = 'block';
        } else {
            this.locationInfo.style.display = 'none';
        }
    }

    // Update product interface
    updateProductInterface(metadata) {
        if (metadata && metadata.products && metadata.products.length > 0) {
            this.productContainer.style.display = 'block';
            
            // Clear existing options
            this.productDropdown.innerHTML = '';
            this.selectedProductIndex = 0;
            
            // Add product options
            metadata.products.forEach((product, index) => {
                const option = document.createElement('option');
                option.value = index;
                option.textContent = product.name;
                this.productDropdown.appendChild(option);
            });
            
            // Update details for first product
            this.updateProductDetails();
        } else {
            this.productContainer.style.display = 'none';
        }
    }

    // Update product details
    updateProductDetails() {
        if (!this.currentImageData || !this.currentImageData.metadata || !this.currentImageData.metadata.products) return;
        
        const selectedProduct = this.currentImageData.metadata.products[this.selectedProductIndex];
        if (selectedProduct) {
            // Fade out current details
            gsap.to([this.priceDisplay, this.productDescription, this.skuDisplay], {
                opacity: 0,
                duration: 0.1,
                ease: "power2.out",
                onComplete: () => {
                    // Update content while faded out
                    this.priceDisplay.textContent = `$${selectedProduct.basePrice.toFixed(2)}`;
                    this.productDescription.textContent = selectedProduct.description || 'Premium quality print';
                    this.skuDisplay.textContent = `SKU: ${selectedProduct.sku}`;
                    
                    // Fade in updated details
                    gsap.to([this.priceDisplay, this.productDescription, this.skuDisplay], {
                        opacity: 1,
                        duration: 0.2,
                        ease: "power2.out"
                    });
                }
            });
        }
    }

    // Create inquiry form UI
    createInquiryForm() {
        this.inquiryForm = document.createElement('div');
        this.inquiryForm.style.cssText = `
            display: none;
            opacity: 0;
            width: 100%;
        `;

        // Name field
        const nameGroup = document.createElement('div');
        nameGroup.style.cssText = `margin-bottom: 15px;`;

        const nameLabel = document.createElement('label');
        nameLabel.innerHTML = 'Name <span style="color: #ff6b6b;">*</span>';
        nameLabel.style.cssText = `
            display: block;
            color: rgba(255, 255, 255, 0.9);
            font-size: 13px;
            margin-bottom: 6px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        this.nameInput = document.createElement('input');
        this.nameInput.type = 'text';
        this.nameInput.placeholder = 'Your name';
        this.nameInput.style.cssText = `
            width: 100%;
            background: rgba(0, 0, 0, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            color: white;
            padding: 10px 12px;
            font-size: 16px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            outline: none;
            transition: border-color 0.2s ease;
            box-sizing: border-box;
        `;

        this.nameInput.addEventListener('focus', () => {
            this.nameInput.style.borderColor = 'rgba(255, 255, 255, 0.4)';
        });

        this.nameInput.addEventListener('blur', () => {
            this.nameInput.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        });

        this.nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.handleInquirySubmit();
            }
        });

        nameGroup.appendChild(nameLabel);
        nameGroup.appendChild(this.nameInput);

        // Email field
        const emailGroup = document.createElement('div');
        emailGroup.style.cssText = `margin-bottom: 15px;`;

        const emailLabel = document.createElement('label');
        emailLabel.innerHTML = 'Email <span style="color: #ff6b6b;">*</span>';
        emailLabel.style.cssText = `
            display: block;
            color: rgba(255, 255, 255, 0.9);
            font-size: 13px;
            margin-bottom: 6px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        this.emailInput = document.createElement('input');
        this.emailInput.type = 'email';
        this.emailInput.placeholder = 'your@email.com';
        this.emailInput.style.cssText = `
            width: 100%;
            background: rgba(0, 0, 0, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            color: white;
            padding: 10px 12px;
            font-size: 16px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            outline: none;
            transition: border-color 0.2s ease;
            box-sizing: border-box;
        `;

        this.emailInput.addEventListener('focus', () => {
            this.emailInput.style.borderColor = 'rgba(255, 255, 255, 0.4)';
        });

        this.emailInput.addEventListener('blur', () => {
            this.emailInput.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        });

        this.emailInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.handleInquirySubmit();
            }
        });

        emailGroup.appendChild(emailLabel);
        emailGroup.appendChild(this.emailInput);

        // Comment field
        const commentGroup = document.createElement('div');
        commentGroup.style.cssText = `margin-bottom: 15px;`;

        const commentLabel = document.createElement('label');
        commentLabel.innerHTML = 'Message <span style="color: #ff6b6b;">*</span>';
        commentLabel.style.cssText = `
            display: block;
            color: rgba(255, 255, 255, 0.9);
            font-size: 13px;
            margin-bottom: 6px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        this.commentInput = document.createElement('textarea');
        this.commentInput.placeholder = 'Your message about this image...';
        this.commentInput.rows = 4;
        this.commentInput.style.cssText = `
            width: 100%;
            background: rgba(0, 0, 0, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            color: white;
            padding: 10px 12px;
            font-size: 16px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            outline: none;
            transition: border-color 0.2s ease;
            resize: vertical;
            box-sizing: border-box;
        `;

        this.commentInput.addEventListener('focus', () => {
            this.commentInput.style.borderColor = 'rgba(255, 255, 255, 0.4)';
        });

        this.commentInput.addEventListener('blur', () => {
            this.commentInput.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        });

        this.commentInput.addEventListener('keydown', (e) => {
            // Ctrl+Enter or Cmd+Enter submits the form
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this.handleInquirySubmit();
            }
        });

        commentGroup.appendChild(commentLabel);
        commentGroup.appendChild(this.commentInput);

        // Error message container
        this.formErrorMessage = document.createElement('div');
        this.formErrorMessage.style.cssText = `
            display: none;
            color: #ff6b6b;
            font-size: 13px;
            margin-bottom: 15px;
            padding: 10px;
            background: rgba(255, 107, 107, 0.1);
            border: 1px solid rgba(255, 107, 107, 0.3);
            border-radius: 4px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
        `;

        // Button container
        const buttonGroup = document.createElement('div');
        buttonGroup.style.cssText = `
            display: flex;
            gap: 10px;
        `;

        // Cancel button
        this.cancelButton = document.createElement('button');
        this.cancelButton.textContent = 'Cancel';
        this.cancelButton.style.cssText = `
            flex: 1;
            background: transparent;
            color: rgba(255, 255, 255, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            padding: 10px 20px;
            cursor: pointer;
            font-size: 14px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            transition: all 0.2s ease;
            outline: none;
        `;

        this.cancelButton.addEventListener('mouseenter', () => {
            this.cancelButton.style.background = 'rgba(255, 255, 255, 0.05)';
            this.cancelButton.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            this.cancelButton.style.color = 'rgba(255, 255, 255, 0.8)';
        });

        this.cancelButton.addEventListener('mouseleave', () => {
            this.cancelButton.style.background = 'transparent';
            this.cancelButton.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            this.cancelButton.style.color = 'rgba(255, 255, 255, 0.6)';
        });

        this.cancelButton.addEventListener('click', () => {
            this.collapseInquiryForm();
        });

        // Send button
        this.sendButton = document.createElement('button');
        this.sendButton.textContent = 'Send';
        this.sendButton.style.cssText = `
            flex: 1;
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            padding: 10px 20px;
            cursor: pointer;
            font-size: 14px;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            transition: all 0.2s ease;
            outline: none;
        `;

        this.sendButton.addEventListener('mouseenter', () => {
            if (!this.formSubmitting) {
                this.sendButton.style.background = 'rgba(255, 255, 255, 0.15)';
                this.sendButton.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                this.sendButton.style.color = 'rgba(255, 255, 255, 1)';
            }
        });

        this.sendButton.addEventListener('mouseleave', () => {
            if (!this.formSubmitting) {
                this.sendButton.style.background = 'rgba(255, 255, 255, 0.1)';
                this.sendButton.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                this.sendButton.style.color = 'rgba(255, 255, 255, 0.9)';
            }
        });

        this.sendButton.addEventListener('click', () => {
            this.handleInquirySubmit();
        });

        buttonGroup.appendChild(this.cancelButton);
        buttonGroup.appendChild(this.sendButton);

        // Assemble form
        this.inquiryForm.appendChild(nameGroup);
        this.inquiryForm.appendChild(emailGroup);
        this.inquiryForm.appendChild(commentGroup);
        this.inquiryForm.appendChild(this.formErrorMessage);
        this.inquiryForm.appendChild(buttonGroup);

        // Add form to inquiry container
        this.inquiryContainer.appendChild(this.inquiryForm);
    }

    // Expand inquiry form with animation and auto-scroll
    expandInquiryForm() {
        if (this.formExpanded) return;

        this.formExpanded = true;

        // Reset form
        this.nameInput.value = '';
        this.emailInput.value = '';
        this.commentInput.value = '';
        this.formErrorMessage.style.display = 'none';

        // Get initial height for animation
        this.inquiryForm.style.display = 'block';
        this.inquiryForm.style.opacity = '0';
        const formHeight = this.inquiryForm.offsetHeight;
        this.inquiryForm.style.height = '0px';

        // Animation timeline
        const tl = gsap.timeline({
            onComplete: () => {
                this.inquiryForm.style.height = 'auto';
                // Auto-scroll to ensure all fields are visible
                this.autoScrollToForm();
            }
        });

        // Fade out button
        tl.to(this.purchaseButton, {
            opacity: 0,
            height: 0,
            padding: 0,
            duration: 0.25,
            ease: 'power2.inOut'
        })
        // Expand and fade in form
        .set(this.inquiryForm, { display: 'block' })
        .to(this.inquiryForm, {
            height: formHeight,
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out'
        }, '-=0.1');
    }

    // Collapse inquiry form back to button
    collapseInquiryForm() {
        if (!this.formExpanded) return;

        this.formExpanded = false;

        const tl = gsap.timeline({
            onComplete: () => {
                this.inquiryForm.style.display = 'none';
                this.purchaseButton.style.height = 'auto';
            }
        });

        // Fade out form
        tl.to(this.inquiryForm, {
            opacity: 0,
            height: 0,
            duration: 0.25,
            ease: 'power2.inOut'
        })
        // Fade in button
        .to(this.purchaseButton, {
            opacity: 1,
            height: 'auto',
            padding: '12px 24px',
            duration: 0.3,
            ease: 'power2.out'
        }, '-=0.1');
    }

    // Auto-scroll to ensure form is visible
    autoScrollToForm() {
        if (!this.content || !this.inquiryContainer) return;

        const containerRect = this.inquiryContainer.getBoundingClientRect();
        const contentRect = this.content.getBoundingClientRect();
        const formBottom = containerRect.bottom;
        const viewportBottom = contentRect.bottom;

        // If form extends below viewport, scroll to reveal it
        if (formBottom > viewportBottom) {
            const scrollAmount = formBottom - viewportBottom + 40; // 40px padding

            gsap.to(this.content, {
                scrollTop: this.content.scrollTop + scrollAmount,
                duration: 0.4,
                ease: 'power2.out'
            });
        }
    }

    // Validate form inputs
    validateForm() {
        const name = this.nameInput.value.trim();
        const email = this.emailInput.value.trim();
        const comment = this.commentInput.value.trim();

        // Check all fields filled
        if (!name || !email || !comment) {
            this.showFormError('Please fill in all required fields.');
            return false;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            this.showFormError('Please enter a valid email address.');
            return false;
        }

        return true;
    }

    // Show form error message
    showFormError(message) {
        this.formErrorMessage.style.color = '#ff6b6b';
        this.formErrorMessage.style.background = 'rgba(255, 107, 107, 0.1)';
        this.formErrorMessage.style.border = '1px solid rgba(255, 107, 107, 0.3)';
        this.formErrorMessage.textContent = message;
        this.formErrorMessage.style.display = 'block';

        gsap.fromTo(this.formErrorMessage,
            { opacity: 0, y: -10 },
            { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
        );
    }

    // Hide form error message
    hideFormError() {
        gsap.to(this.formErrorMessage, {
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
                this.formErrorMessage.style.display = 'none';
            }
        });
    }

    // Handle form submission
    async handleInquirySubmit() {
        if (this.formSubmitting) return;
        if (!this.validateForm()) return;

        this.formSubmitting = true;
        this.hideFormError();

        this.sendButton.disabled = true;
        this.sendButton.style.opacity = '0.6';
        this.sendButton.style.cursor = 'not-allowed';
        this.sendButton.textContent = 'Sending…';

        try {
            const { getTurnstileToken } = await import('../utils/TurnstileWidget.js');
            const turnstileToken = await getTurnstileToken();

            // Build subject from image + sheet context
            const imageTitle = this.currentImageData?.metadata?.title || this.title?.textContent || 'Unknown';
            const sheetId = this.contactSheet?.sheetId || '';
            const { getSheetData } = await import('../config/SheetConfig.js');
            const sheetData = sheetId ? await getSheetData(sheetId) : null;
            const sheetTitle = sheetData?.title || '';
            const subject = `Inquiry: ${imageTitle}` + (sheetTitle ? ` — ${sheetTitle}` : '');

            // Get selected product info
            const selectedProduct = this.currentImageData?.metadata?.products?.[this.selectedProductIndex];

            const res = await fetch('/api/send-inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: this.nameInput.value.trim(),
                    email: this.emailInput.value.trim(),
                    message: this.commentInput.value.trim(),
                    turnstileToken,
                    subject,
                    imageTitle,
                    productName: selectedProduct?.name || '',
                    productSku: selectedProduct?.sku || '',
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Failed to send message.');
            }

            this.showFormResult('success');
        } catch (err) {
            this.showFormResult('error', err.message || 'Something went wrong. Please try again.');
        } finally {
            this.formSubmitting = false;
            this.sendButton.disabled = false;
            this.sendButton.style.opacity = '1';
            this.sendButton.style.cursor = 'pointer';
            this.sendButton.textContent = 'Send';
        }
    }

    showFormResult(type, errorMessage) {
        const isSuccess = type === 'success';
        const color = isSuccess ? '#6fdb6f' : '#ff6b6b';
        const label = isSuccess ? 'Message sent' : (errorMessage || 'Failed to send');

        const iconPath = isSuccess
            ? '<polyline points="20,31 27,38 40,23" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="29" stroke-dashoffset="29" style="visibility: hidden;" />'
            : '<line x1="22" y1="22" x2="38" y2="38" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="23" stroke-dashoffset="23" /><line x1="38" y1="22" x2="22" y2="38" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="23" stroke-dashoffset="23" />';

        // Hide all direct children of the form
        Array.from(this.inquiryForm.children).forEach(el => {
            el.style.display = 'none';
        });

        // Create result overlay
        let resultEl = this.inquiryForm.querySelector('.inquiry-form-result');
        if (!resultEl) {
            resultEl = document.createElement('div');
            resultEl.className = 'inquiry-form-result';
            this.inquiryForm.appendChild(resultEl);
        }

        resultEl.style.cssText = `
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 40px 20px; min-height: 200px;
        `;
        resultEl.innerHTML = `
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" style="color: ${color};">
                <circle cx="30" cy="30" r="26" stroke="currentColor" stroke-width="2"
                    stroke-dasharray="163.36" stroke-dashoffset="163.36" />
                ${iconPath}
            </svg>
            <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin-top: 16px;
                font-family: 'IBM Plex Mono', monospace; opacity: 0;">${label}</p>
        `;

        const circle = resultEl.querySelector('circle');
        const text = resultEl.querySelector('p');

        gsap.to(circle, { strokeDashoffset: 0, duration: 0.6, ease: 'power2.out' });

        if (isSuccess) {
            const check = resultEl.querySelector('polyline');
            gsap.set(check, { visibility: 'visible', delay: 0.5 });
            gsap.to(check, { strokeDashoffset: 0, duration: 0.4, delay: 0.5, ease: 'power2.out' });
        } else {
            const lines = resultEl.querySelectorAll('line');
            gsap.to(lines[0], { strokeDashoffset: 0, duration: 0.3, delay: 0.5, ease: 'power2.out' });
            gsap.to(lines[1], { strokeDashoffset: 0, duration: 0.3, delay: 0.65, ease: 'power2.out' });
        }

        gsap.to(text, { opacity: 1, duration: 0.3, delay: 0.8, ease: 'power2.out' });

        if (isSuccess) {
            // Clear form fields
            this.nameInput.value = '';
            this.emailInput.value = '';
            this.commentInput.value = '';
        }

        // Auto-restore after delay
        setTimeout(() => {
            this.restoreInquiryForm();
        }, isSuccess ? 3000 : 4000);
    }

    restoreInquiryForm() {
        const resultEl = this.inquiryForm.querySelector('.inquiry-form-result');
        if (resultEl) {
            gsap.to(resultEl, {
                opacity: 0, duration: 0.3, ease: 'power2.in',
                onComplete: () => {
                    resultEl.remove();
                    // Re-show all direct children (field groups, error container, button group)
                    Array.from(this.inquiryForm.children).forEach(el => {
                        el.style.display = '';
                    });
                    // Error message should stay hidden until needed
                    this.formErrorMessage.style.display = 'none';
                    // Collapse the form back
                    this.collapseInquiryForm();
                }
            });
        }
    }

    // Handle mobile navigation - ROUTES THROUGH NAVIGATION CONTROLLER
    handleMobileNavigation(direction) {
        console.log(`DetailView: Mobile ${direction} navigation. Routing to NC.`);
        
        // Route through NavigationController - SAME AS EXISTING INTERACTIONS
        if (this.navigationController) {
            this.navigationController.navigate(direction, { source: 'mobile-hint' });
        } else {
            // Fallback - same as existing navigation
            this.navigateLinear(direction === 'left' ? 'previous' : 'next');
        }
    }

    // Flash button on edge tap for visual feedback
    flashButton(direction) {
        const button = direction === 'left' ? this.leftNavButton : this.rightNavButton;
        
        gsap.killTweensOf(button);
        gsap.set(button, { opacity: 0.4, scale: 1.1 });
        gsap.to(button, {
            opacity: 0.15,
            scale: 1,
            duration: 0.3,
            ease: 'power2.out'
        });
    }

}